/**
 * The clock a play head must run on.
 *
 * A play head driven by `performance.now()` at the moment `play()` is called is displaced from the
 * audio by every offset the audio path adds after that moment, and there are three:
 *
 *  1. `SCHEDULE_LEAD_MS` (120 ms) - the lead `toTransportTimes` adds so a note is not scheduled
 *     inside the transport's own lookahead.
 *  2. The Tone context's `lookAhead` (0.1 s by default, and never configured here). Tone resolves
 *     a `+X` transport time against `context.now()`, which is `currentTime + lookAhead` - so the
 *     lead above is measured from a moment that is itself already 100 ms in the future.
 *  3. The output latency of the device - the hardware buffer between a sample being written and
 *     being heard.
 *
 * Subtracting a constant for those would be wrong twice over: the first two are configuration that
 * can change, and the third is hardware that differs per machine. Instead the play head reads the
 * audio clock directly, so all three are accounted for by construction.
 */

/** The fields of `AudioTimestamp` this module reads. */
export interface OutputTimestamp {
  contextTime?: number
  performanceTime?: number
}

/**
 * The parts of an AudioContext the output clock needs.
 *
 * `getOutputTimestamp` is optional because it genuinely may not be there: it is absent from
 * `standardized-audio-context`'s wrapper (which is what this app runs on), and older Safari and
 * Firefox lack it natively. Every one of those falls back to `currentTime`.
 */
export interface OutputClockContext {
  currentTime: number
  getOutputTimestamp?: () => OutputTimestamp
}

/**
 * The audio clock, in context seconds, as close to what is being HEARD as the platform allows.
 *
 * `getOutputTimestamp()` is the accurate reading: its `contextTime` is the sample frame leaving the
 * device right now, so it has the output latency already taken out, and pairing it with
 * `performanceTime` lets the value be interpolated between the timestamp's own coarse updates
 * instead of stepping. `currentTime` is the fallback: it is the sample frame being WRITTEN, so a
 * play head on it still leads the sound by the output latency - much less wrong than the 220 ms of
 * (1) and (2), but not zero.
 *
 * A zero or negative reading means the context has not rendered yet, when the pair is all zeros and
 * interpolating from it would produce nonsense; `currentTime` is ~0 then too, so the fallback is
 * also the correct answer.
 */
export const outputTimeAt = (context: OutputClockContext, performanceNowMs: number): number => {
  const timestamp = context.getOutputTimestamp?.()
  const contextTime = timestamp?.contextTime
  const performanceTime = timestamp?.performanceTime
  if (contextTime === undefined || performanceTime === undefined || contextTime <= 0 || performanceTime <= 0) {
    return context.currentTime
  }
  return contextTime + (performanceNowMs - performanceTime) / 1000
}

/**
 * Where the play head sits, in ms from the start of the timeline, given the audio clock now and the
 * context time the timeline's t=0 sounds at.
 *
 * Clamped at 0: `startSeconds` is in the future at the moment playback is requested, and the play
 * head must sit at the start until the first note is actually audible rather than run ahead of it.
 */
export const playheadMsAt = (audioNowSeconds: number, startSeconds: number): number =>
  Math.max(0, (audioNowSeconds - startSeconds) * 1000)
